/**
 * Rectangle - Axis-Aligned Bounding Box (AABB)
 *
 * Represents a rectangle used for collision detection, viewports, and UI elements.
 * The rectangle is "axis-aligned" meaning its edges are parallel to the X and Y axes.
 *
 * YOUR TASK: Complete the TODO sections
 *
 * TypeScript Concepts Demonstrated:
 * - Getters (computed properties)
 * - Static factory methods
 * - Nullable return types (Rectangle | null)
 * - Array return types
 *
 * @example
 * const rect = new Rectangle(10, 20, 100, 50);
 * if (rect.contains(mousePosition)) {
 *   console.log('Mouse is inside!');
 * }
 */

import { Vector2 } from './Vector2';

export class Rectangle {
  /**
   * The X coordinate of the left edge
   */
  public x: number;

  /**
   * The Y coordinate of the top edge
   */
  public y: number;

  /**
   * The width of the rectangle
   */
  public width: number;

  /**
   * The height of the rectangle
   */
  public height: number;

  /**
   * Create a new Rectangle
   * @param x - Left edge X coordinate
   * @param y - Top edge Y coordinate
   * @param width - Width of rectangle
   * @param height - Height of rectangle
   */
  constructor(x: number = 0, y: number = 0, width: number = 0, height: number = 0) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
  }

  // ============================================
  // GETTERS (Computed Properties)
  // TypeScript getters let you access computed values like properties
  // ============================================

  /**
   * Get the left edge X coordinate
   */
  get left(): number {
    return this.x;
  }

  /**
   * Get the right edge X coordinate
   */
  get right(): number {
    // TODO: Implement this getter
    return this.x + this.width;
  }

  /**
   * Get the top edge Y coordinate
   */
  get top(): number {
    return this.y;
  }

  /**
   * Get the bottom edge Y coordinate
   */
  get bottom(): number {
    // TODO: Implement this getter
    return this.y + this.height;
  }

  /**
   * Get the center X coordinate
   */
  get centerX(): number {
    return this.x + this.width / 2;
  }

  /**
   * Get the center Y coordinate
   */
  get centerY(): number {
    return this.y + this.height / 2;
  }

  // ============================================
  // STATIC FACTORY METHODS
  // ============================================

  /**
   * Create a rectangle from two corner points
   * @param topLeft - Top-left corner
   * @param bottomRight - Bottom-right corner
   */
  static fromPoints(topLeft: Vector2, bottomRight: Vector2): Rectangle {
    // TODO: Implement this method
    // Handle cases where points might be "inverted" (bottomRight is actually top-left)
    const x = Math.min(topLeft.x, bottomRight.x);
    const y = Math.min(topLeft.y, bottomRight.y);
    const width = Math.abs(bottomRight.x - topLeft.x);
    const height = Math.abs(bottomRight.y - topLeft.y);

    return new Rectangle(x, y, width, height);
  }

  /**
   * Create a rectangle from center point and dimensions
   * @param center - Center point
   * @param width - Width of rectangle
   * @param height - Height of rectangle
   */
  static fromCenter(center: Vector2, width: number, height: number): Rectangle {
    // TODO: Implement this method
    return new Rectangle(
      center.x - width / 2,
      center.y - height / 2,
      width,
      height
    );
  }

  /**
   * Create a zero rectangle at origin
   */
  static zero(): Rectangle {
    return new Rectangle(0, 0, 0, 0);
  }

  // ============================================
  // CONTAINMENT METHODS
  // ============================================

  /**
   * Check if a point is inside this rectangle
   * @param point - The point to check
   * @returns True if the point is inside (including edges)
   */
  contains(point: Vector2): boolean {
    // TODO: Implement this method
    return (
      point.x >= this.left &&
      point.x <= this.right &&
      point.y >= this.top &&
      point.y <= this.bottom
    );
  }

  /**
   * Check if another rectangle is completely inside this one
   * @param other - The rectangle to check
   * @returns True if the other rectangle is completely contained
   */
  containsRect(other: Rectangle): boolean {
    // TODO: Implement this method
    return (
      other.left >= this.left &&
      other.right <= this.right &&
      other.top >= this.top &&
      other.bottom <= this.bottom
    );
  }

  // ============================================
  // INTERSECTION METHODS
  // ============================================

  /**
   * Check if this rectangle intersects (overlaps) with another
   * @param other - The rectangle to check
   * @returns True if the rectangles overlap
   */
  intersects(other: Rectangle): boolean {
    // TODO: Implement this method
    // Two rectangles intersect if they overlap on BOTH axes
    // They DON'T intersect if one is completely to the left, right, above, or below
    return (
      this.left < other.right &&
      this.right > other.left &&
      this.top < other.bottom &&
      this.bottom > other.top
    );
  }

  /**
   * Get the intersection (overlap area) with another rectangle
   * @param other - The rectangle to intersect with
   * @returns The overlapping rectangle, or null if no overlap
   *
   * TypeScript Learning: Nullable return types with | null
   */
  intersection(other: Rectangle): Rectangle | null {
    // TODO: Implement this method
    if (!this.intersects(other)) {
      return null;
    }

    const x = Math.max(this.left, other.left);
    const y = Math.max(this.top, other.top);
    const right = Math.min(this.right, other.right);
    const bottom = Math.min(this.bottom, other.bottom);

    return new Rectangle(x, y, right - x, bottom - y);
  }

  /**
   * Get the smallest rectangle that contains both rectangles
   * @param other - The rectangle to union with
   * @returns A new rectangle containing both
   */
  union(other: Rectangle): Rectangle {
    // TODO: Implement this method
    const x = Math.min(this.left, other.left);
    const y = Math.min(this.top, other.top);
    const right = Math.max(this.right, other.right);
    const bottom = Math.max(this.bottom, other.bottom);

    return new Rectangle(x, y, right - x, bottom - y);
  }

  // ============================================
  // TRANSFORMATION METHODS
  // ============================================

  /**
   * Create a new rectangle expanded by an amount on all sides
   * @param amount - How much to expand (negative to shrink)
   * @returns A new expanded rectangle
   */
  expand(amount: number): Rectangle {
    // TODO: Implement this method
    return new Rectangle(
      this.x - amount,
      this.y - amount,
      this.width + amount * 2,
      this.height + amount * 2
    );
  }

  /**
   * Create a new rectangle translated by a vector
   * @param offset - The translation vector
   * @returns A new translated rectangle
   */
  translate(offset: Vector2): Rectangle {
    return new Rectangle(
      this.x + offset.x,
      this.y + offset.y,
      this.width,
      this.height
    );
  }

  /**
   * Create a new rectangle with a different height
   * @param newHeight - The new height
   * @returns A new rectangle with the specified height
   */
  withHeight(newHeight: number): Rectangle {
    return new Rectangle(this.x, this.y, this.width, newHeight);
  }

  /**
   * Create a new rectangle with a different width
   * @param newWidth - The new width
   * @returns A new rectangle with the specified width
   */
  withWidth(newWidth: number): Rectangle {
    return new Rectangle(this.x, this.y, newWidth, this.height);
  }

  /**
   * Create a new rectangle scaled from its center
   * @param scaleX - X scale factor
   * @param scaleY - Y scale factor (defaults to scaleX for uniform scaling)
   * @returns A new scaled rectangle
   */
  scale(scaleX: number, scaleY: number = scaleX): Rectangle {
    const center = this.getCenter();
    const newWidth = this.width * scaleX;
    const newHeight = this.height * scaleY;

    return Rectangle.fromCenter(center, newWidth, newHeight);
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Get the center point of the rectangle
   * @returns The center as a Vector2
   */
  getCenter(): Vector2 {
    // TODO: Implement this method
    return new Vector2(this.centerX, this.centerY);
  }

  /**
   * Get all four corners of the rectangle
   * @returns Array of corners: [topLeft, topRight, bottomRight, bottomLeft]
   */
  getCorners(): Vector2[] {
    // TODO: Implement this method
    return [
      new Vector2(this.left, this.top),      // Top-left
      new Vector2(this.right, this.top),     // Top-right
      new Vector2(this.right, this.bottom),  // Bottom-right
      new Vector2(this.left, this.bottom)    // Bottom-left
    ];
  }

  /**
   * Get the size of the rectangle as a vector
   * @returns Width and height as a Vector2
   */
  getSize(): Vector2 {
    return new Vector2(this.width, this.height);
  }

  /**
   * Get the area of the rectangle
   * @returns The area (width * height)
   */
  getArea(): number {
    return this.width * this.height;
  }

  /**
   * Get the perimeter of the rectangle
   * @returns The perimeter (2 * width + 2 * height)
   */
  getPerimeter(): number {
    return 2 * this.width + 2 * this.height;
  }

  /**
   * Check if this rectangle has zero area
   * @returns True if width or height is zero
   */
  isEmpty(): boolean {
    return this.width === 0 || this.height === 0;
  }

  /**
   * Create a copy of this rectangle
   * @returns A new rectangle with the same values
   */
  clone(): Rectangle {
    // TODO: Implement this method
    return new Rectangle(this.x, this.y, this.width, this.height);
  }

  /**
   * Check if this rectangle equals another
   * @param other - The rectangle to compare
   * @returns True if all properties match
   */
  equals(other: Rectangle): boolean {
    // TODO: Implement this method
    return (
      this.x === other.x &&
      this.y === other.y &&
      this.width === other.width &&
      this.height === other.height
    );
  }

  /**
   * Convert to string for debugging
   * @returns String in format "Rectangle(x, y, width, height)"
   */
  toString(): string {
    return `Rectangle(${this.x}, ${this.y}, ${this.width}, ${this.height})`;
  }

  // ============================================
  // COLLISION HELPERS
  // ============================================

  /**
   * Get the closest point on this rectangle to an external point
   * Useful for collision detection and distance calculations
   *
   * @param point - The external point
   * @returns The closest point on the rectangle's perimeter
   */
  closestPoint(point: Vector2): Vector2 {
    const x = Math.max(this.left, Math.min(point.x, this.right));
    const y = Math.max(this.top, Math.min(point.y, this.bottom));
    return new Vector2(x, y);
  }

  /**
   * Get the distance from a point to the nearest edge of this rectangle
   * Returns 0 if the point is inside the rectangle
   *
   * @param point - The point to check
   * @returns Distance to nearest edge (0 if inside)
   */
  distanceToPoint(point: Vector2): number {
    if (this.contains(point)) {
      return 0;
    }
    const closest = this.closestPoint(point);
    return point.distance(closest);
  }
}
